import { useMemo, useState, useEffect } from 'react';
import cn from 'classnames';
import { useTable, useSortBy, usePagination, Column } from 'react-table';

import { AppHeader } from '../components/AppHeader';
import Card from '../components/Card';
import Chart, {
  getCountDateValues,
  getLineTotalCount,
  ChartLineData,
  ChartLineKeys,
} from '../components/Chart';
import Container from '../components/Container';
import { IconCSV, IconTableSort } from '../components/Icons';
import MonthYearSelector from '../components/MonthYearSelector';
import Select from '../components/Select';
import { Table, Pagination } from '../components/Table';

import { useAccountUser } from '../contexts/User';

import {
  lineDataToCSVValueArray,
  translationTableDataToCSV,
  objectsToCSV,
  downloadCSV,
} from '../utils/export';
import { shortMonths, longMonths } from '../utils/time';
import * as portalApi from '../utils/api';
import { pink, yellow, green, orange, lightBlue, red } from '../utils/color';
import getAbbreviatedNum from '../utils/format';

type LineKey =
  | 'requestCount'
  | 'requestScriptureCount'
  | 'requestVerseCount'
  | 'viewCount'
  | 'viewVerseCount'
  | 'viewUserCount';

type MetricMeta = {
  // eslint-disable-next-line no-unused-vars
  [lineKey in LineKey]: { label: string; color: string };
};

const lineMetadata: MetricMeta = {
  requestCount: { label: 'API Requests', color: pink },
  requestScriptureCount: { label: 'Scripture Requests', color: yellow },
  requestVerseCount: { label: 'Verses Requested', color: green },
  viewCount: { label: 'Views', color: orange },
  viewVerseCount: { label: 'Verses Viewed', color: lightBlue },
  viewUserCount: { label: 'Users', color: red },
};

export default function ApplicationReport({ appId }: { appId: string }) {
  const { account } = useAccountUser();

  const [lineData, setLineData] = useState<ChartLineData | undefined>();
  const [lineError, setLineError] = useState<any>();
  const [activeLine, setActiveLine] = useState<ChartLineKeys>('requestCount');

  const [tableData, setTableData] = useState<any | undefined>();
  const [tableError, setTableError] = useState<any>();

  const [[month, year], setTimeRange] = useState([
    new Date().getMonth() + 1,
    new Date().getFullYear(),
  ]);

  const startDate = month === 0 ? `${year}` : `${year}-${(month < 10 ? '0' : '') + month}`;
  const nextMonth = month === 12 ? 1 : month + 1;
  const endDate =
    month === 0
      ? `${year + 1}`
      : `${nextMonth < month ? year + 1 : year}-${(nextMonth < 10 ? '0' : '') + nextMonth}`;

  useEffect(() => {
    setLineData(undefined);
    setLineError(undefined);
    setTableData(undefined);
    setTableError(undefined);

    if (account) {
      portalApi
        .getAppReport({
          accountId: account.id,
          appId,
          startDate,
          endDate,
          summary: month === 0 ? 'year' : 'month',
        })
        .then(({ analytics, translations }) => {
          setLineData(
            Object.entries(lineMetadata).reduce((lineDatum, [lineKey, lineMeta]) => {
              const data = getCountDateValues(analytics[lineKey as LineKey]);
              return { ...lineDatum, [lineKey]: { data, ...lineMeta } };
            }, {} as ChartLineData),
          );
          setTableData(translations);
        })
        .catch((err: any) => {
          setLineError(err);
          setTableError(err);
        });
    }
  }, [month, year, account, appId, endDate, startDate]);

  const totals = Object.keys(lineMetadata).reduce((lineTotals, lineKey) => {
    const total = getLineTotalCount(lineData?.[lineKey]);
    // eslint-disable-next-line no-param-reassign
    lineTotals[lineKey] = total;
    return lineTotals;
  }, {} as { [lineKey: string]: number });

  const columns = useMemo<Column<ReportDatum>[]>(
    () => [
      { Header: 'Translation', accessor: 'name' },
      {
        accessor: 'requestCount',
        Header: 'API Requests',
      },
      {
        accessor: 'requestScriptureCount',
        Header: 'Scripture Requests',
      },
      {
        accessor: 'requestVerseCount',
        Header: 'Verses Requested',
      },
      { accessor: 'viewCount', Header: 'Views' },
      { accessor: 'viewVerseCount', Header: 'Verses Viewed' },
      { accessor: 'viewUserCount', Header: 'Users' },
    ],
    [],
  );

  const fileLabel = month >= 0 ? `${year}-${shortMonths[month - 1]}` : year;

  return (
    <Container>
      <AppHeader appLinkBasePath="/application" currentAppId={appId} currentAppMenuItem="reports" />
      <div className="flex justify-between mb-4">
        <MonthYearSelector onChange={(monthYear) => setTimeRange(monthYear)} />

        <Select label="Export" origin="right">
          <button
            type="button"
            className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 hover:text-blue-500"
            disabled={!lineData}
            onClick={() => {
              if (lineData === undefined) return;
              const csvArray = lineDataToCSVValueArray(lineData);
              // eslint-disable-next-line no-unused-vars
              const csv = objectsToCSV<{ [lineKey in LineKey]: number } & { date: string }>(
                ['date', ...(Object.keys(lineData) as LineKey[])],
                csvArray,
              );

              downloadCSV(`${fileLabel}-app-analytics.csv`, csv);
            }}
          >
            <IconCSV className="inline w-5 h-5 mr-1" /> App Analytics
          </button>
          <button
            type="button"
            className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 hover:text-blue-500"
            onClick={() => {
              if (tableData === undefined) return;
              const csv = translationTableDataToCSV(columns, tableData as unknown as ReportDatum[]);
              downloadCSV(`${fileLabel}-translation-usage.csv`, csv);
            }}
          >
            <IconCSV className="inline w-5 h-5 mr-1" /> Translation Usage
          </button>
        </Select>
      </div>
      <Card dark className="mb-8">
        {/* Grand Totals */}
        <div className="px-8 pt-2 pb-3 text-white">
          <ul className="flex flex-wrap">
            {!lineData && !lineError && (
              <li className="w-full">
                <h3 className="text-lg text-center">Loading...</h3>
              </li>
            )}
            {lineError && (
              <li className="w-full">
                <h3 className="text-lg text-center text-red-500">Error loading data</h3>
              </li>
            )}
            {lineData &&
              !lineError &&
              Object.keys(lineData).map((lineKey) => (
                <li className="mb-3 mr-7 sm:mr-10" key={lineKey}>
                  <button
                    className={cn('text-left focus:outline-none')}
                    type="button"
                    onClick={() => setActiveLine(lineKey)}
                  >
                    <span
                      style={{
                        color: lineData[lineKey].color,
                        borderColor: lineData[lineKey].color,
                      }}
                      className={cn('font-bold text-lg md:text-xl mb-1', {
                        'border-b-4': activeLine === lineKey,
                      })}
                    >
                      {lineData[lineKey].label}
                    </span>
                    <h3 className="mt-1 text-2xl font-bold sm:text-4xl lg:text-4.5xl lg:tracking-wide mt-3">
                      {getAbbreviatedNum(totals[lineKey])}
                    </h3>
                  </button>
                </li>
              ))}
          </ul>
        </div>
        {/* Chart */}
        {lineData !== undefined && !lineError && (
          <>
            <div className="">
              <Chart width={1218} height={320} lineData={lineData} activeLine={activeLine} />
            </div>
            <div className="border-t border-blue-500 text-white -mt-px pt-2.5 px-4 sm:px-8">
              <div className="flex justify-between">
                <p className="uppercase" style={{ color: '#B5D2FF' }}>
                  {month > 0 && longMonths[month - 1]} {year}
                </p>
                <p className="uppercase" style={{ color: '#B5D2FF' }}>
                  {month > 0 ? (month === 12 ? longMonths[0] : longMonths[month]) : ''}{' '}
                  {month === 0 || month === 12 ? year + 1 : year}
                </p>
              </div>
            </div>
          </>
        )}
      </Card>

      <Card className="px-4 overflow-x-auto">
        {tableData && !tableError && (
          <TranslationsTable
            columns={columns}
            data={tableData}
            viewUserTotal={totals.viewUserCount}
          />
        )}
        {!tableData && !tableError && (
          <div className="w-full text-lg text-center text-gray-500">Loading...</div>
        )}
        {tableError && (
          <div className="w-full text-lg text-center text-red-500">Error loading data.</div>
        )}
      </Card>
    </Container>
  );
}

export interface ReportDatum {
  id: string;
  name: string;
  requestCount: number;
  requestScriptureCount: number;
  requestVerseCount: number;
  viewCount: number;
  viewUserCount: number;
  viewVerseCount: number;
}

type ReportDatumCountKeys = keyof Omit<ReportDatum, 'id' | 'name'>;

const getLocaleSum = (data: ReportDatum[], key: ReportDatumCountKeys): string =>
  data.reduce((sum, d) => sum + d[key], 0).toLocaleString();

function TranslationsTable({
  columns,
  data,
  viewUserTotal,
}: {
  columns: Column<ReportDatum>[];
  data: ReportDatum[];
  viewUserTotal: number;
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    // pagination options
    canPreviousPage,
    canNextPage,
    // pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    // setPageSize,
    state: { pageIndex /* pageSize */ },
  } = useTable(
    {
      columns,
      data,
    },
    useSortBy,
    usePagination,
  );

  return (
    <>
      <Table {...getTableProps()} className="w-full">
        <thead className="text-left">
          <tr className="">
            <th className="sticky left-0 max-w-xs p-3 truncate bg-white">
              <span className="text-2xl sm:text-3xl">{data.length.toLocaleString()}</span>
              <div className="font-normal text-gray-600">Translations</div>
              <div className="absolute right-0 w-px h-full bg-gray-300 top-4" />
            </th>
            {Object.keys(lineMetadata).map((key) => {
              if (key === 'viewUserCount') {
                return (
                  <th className="p-3 text-right" key={key}>
                    <span className="text-2xl sm:text-3xl">{viewUserTotal.toLocaleString()}</span>
                    <div className="font-normal text-gray-600">
                      {lineMetadata[key as ReportDatumCountKeys].label}
                    </div>
                  </th>
                );
              }

              return (
                <th className="p-3 text-right" key={key}>
                  <span className="text-2xl sm:text-3xl">
                    {getLocaleSum(data, key as ReportDatumCountKeys)}
                  </span>
                  <div className="font-normal text-gray-600">
                    {lineMetadata[key as ReportDatumCountKeys].label}
                  </div>
                </th>
              );
            })}
          </tr>
          {headerGroups.map((headerGroup) => (
            <tr className="border-t border-gray-300" {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => (
                <th
                  className={cn('p-3', {
                    'text-right': index > 0,
                    'sticky left-0 bg-white max-w-xs truncate': index === 0,
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  <div
                    className={cn('inline-flex whitespace-nowrap', {
                      'flex-end text-right': index > 0,
                    })}
                  >
                    {column.render('Header')}
                    <IconTableSort
                      className="inline-block w-6 h-6 -mr-2"
                      topFill={column.isSorted && column.isSortedDesc ? 'grey' : 'black'}
                      bottomFill={column.isSorted && !column.isSortedDesc ? 'grey' : 'black'}
                    />
                    {index === 0 && (
                      <div className="absolute top-0 right-0 w-px h-full bg-gray-300" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="text-gray-600" {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr className="border-t border-gray-300" {...row.getRowProps()}>
                {row.cells.map((cell, index) => (
                  <td
                    className={cn('px-3 py-2 whitespace-nowrap', {
                      'sticky left-0 bg-white max-w-xs truncate': index === 0,
                      'text-right': index > 0,
                    })}
                    {...cell.getCellProps()}
                  >
                    {cell.render('Cell')}
                    {index === 0 && (
                      <div className="absolute top-0 right-0 w-px h-full bg-gray-300" />
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
      {pageCount > 1 && (
        <Pagination
          className="flex justify-center"
          pageCount={pageCount}
          pageIndex={pageIndex}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      )}
    </>
  );
}
